import cx from 'classnames';
import React, { useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { getDateFromString } from '../../../../../utility';
import { usePermission } from '../../../../../container/permissionContext';
import EnterCensusModal from '../EnterCensusModal';

const SchedulingDay = ({ periodDay, index, handleClickPrintSchedule, periodData, setPeriodData }) => {
  const { hasPermissionByCodeName, isSuperAdmin } = usePermission();
  const hasWritePermission = hasPermissionByCodeName('scheduling_management');
  const dayNames = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

  const date = getDateFromString(periodDay.date);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dayOfWeek = dayNames[date.getDay()];

  const [isCensusModalOpen, setIsCensusModalOpen] = useState(false);

  const censusValue = useMemo(
    () => (periodData?.units || []).reduce((sum, unit) => sum + (Number(unit.census_by_date?.[periodDay.date]) || 0), 0),
    [periodData?.units, periodDay.date],
  );

  const ppdValue = useMemo(() => {
    let shiftHours = 0;

    periodData?.units?.forEach((unit) => {
      unit.roles.forEach((role) => {
        role.cells.forEach((cell) => {
          if (cell.day === periodDay.date) {
            if (cell.both_halves_user && !cell.both_halves_user.is_off) {
              shiftHours += cell.both_halves_user.hours;
              shiftHours += cell.both_halves_user.minutes / 60;
            }
            if (cell.first_half_user && !cell.first_half_user.is_off) {
              shiftHours += cell.first_half_user.hours;
              shiftHours += cell.first_half_user.minutes / 60;
            }
            if (cell.second_half_user && !cell.second_half_user.is_off) {
              shiftHours += cell.second_half_user.hours;
              shiftHours += cell.second_half_user.minutes / 60;
            }
          }
        });
      });
    });

    shiftHours = Math.round(shiftHours * 100) / 100;

    const result = shiftHours > 0 && Number(censusValue) > 0 ? parseFloat((shiftHours / Number(censusValue)).toFixed(2)) : 0;

    return parseFloat(result.toFixed(2));
  }, [periodData, censusValue]);

  return (
    <th
      key={`${month}-${day}-${dayOfWeek}`}
      colSpan="3"
      className={cx('date-col', {
        'is-weekend': dayOfWeek === 'SA' || dayOfWeek === 'SU',
        'has-red-border': index > 0 && dayOfWeek === 'SU',
      })}
      id={`shift-date-${month}-${day}`}
    >
      <div className="date-col-inner-wrapper">
        <div className="date-residents-wrapper">
          {isMobile || !hasWritePermission ? (
            <span className="census-value">
              <span style={{ color: '#667085' }}>Census:</span> {censusValue}
            </span>
          ) : (
            <button
              type="button"
              className="btn census-btn"
              onClick={() => setIsCensusModalOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_16593_79555)">
                  <path
                    d="M11.3332 14V12.6667C11.3332 11.9594 11.0522 11.2811 10.5521 10.781C10.052 10.281 9.37375 10 8.6665 10H3.33317C2.62593 10 1.94765 10.281 1.44755 10.781C0.947456 11.2811 0.666504 11.9594 0.666504 12.6667V14M15.3332 14V12.6667C15.3327 12.0758 15.1361 11.5018 14.7741 11.0349C14.4121 10.5679 13.9053 10.2344 13.3332 10.0867M10.6665 2.08667C11.2401 2.23353 11.7485 2.56713 12.1116 3.03487C12.4747 3.50261 12.6717 4.07789 12.6717 4.67C12.6717 5.26211 12.4747 5.83739 12.1116 6.30513C11.7485 6.77287 11.2401 7.10647 10.6665 7.25333M8.6665 4.66667C8.6665 6.13943 7.4726 7.33333 5.99984 7.33333C4.52708 7.33333 3.33317 6.13943 3.33317 4.66667C3.33317 3.19391 4.52708 2 5.99984 2C7.4726 2 8.6665 3.19391 8.6665 4.66667Z"
                    stroke="#101828"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_16593_79555">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <span style={{ fontSize: 14 }}>Census</span> <span style={{ fontSize: 14 }}>{censusValue}</span>
            </button>
          )}
          <span>
            PPD<span className="ppd-value"> {ppdValue}</span>
          </span>
        </div>
        {month}/{day} {dayOfWeek}{' '}
        {!isMobile && (
          <button className="btn print-schedule-btn" onClick={() => handleClickPrintSchedule(`${month}/${day} ${dayOfWeek}`)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_13538_91499)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.33329 1.33073C3.33329 0.962539 3.63177 0.664062 3.99996 0.664062H12C12.3681 0.664062 12.6666 0.962539 12.6666 1.33073V5.33073H13.3333C13.8637 5.33073 14.3724 5.54144 14.7475 5.91652C15.1226 6.29159 15.3333 6.80029 15.3333 7.33073V10.6641C15.3333 11.1945 15.1226 11.7032 14.7475 12.0783C14.3724 12.4533 13.8637 12.6641 13.3333 12.6641H12.6666V14.6641C12.6666 15.0322 12.3681 15.3307 12 15.3307H3.99996C3.63177 15.3307 3.33329 15.0322 3.33329 14.6641V12.6641H2.66663C2.13619 12.6641 1.62748 12.4533 1.25241 12.0783C0.87734 11.7032 0.666626 11.1945 0.666626 10.6641V7.33073C0.666626 6.8003 0.87734 6.29159 1.25241 5.91652C1.62748 5.54144 2.13619 5.33073 2.66663 5.33073H3.33329V1.33073ZM4.66663 13.9974H11.3333V9.99739H4.66663V13.9974ZM12.6666 11.3307V9.33073C12.6666 8.96254 12.3681 8.66406 12 8.66406H3.99996C3.63177 8.66406 3.33329 8.96254 3.33329 9.33073V11.3307H2.66663C2.48981 11.3307 2.32025 11.2605 2.19522 11.1355C2.0702 11.0104 1.99996 10.8409 1.99996 10.6641V7.33073C1.99996 7.15392 2.0702 6.98435 2.19522 6.85932C2.32025 6.7343 2.48981 6.66406 2.66663 6.66406H13.3333C13.5101 6.66406 13.6797 6.7343 13.8047 6.85932C13.9297 6.98435 14 7.15392 14 7.33073V10.6641C14 10.8409 13.9297 11.0104 13.8047 11.1355C13.6797 11.2605 13.5101 11.3307 13.3333 11.3307H12.6666ZM11.3333 1.9974V5.33073H4.66663V1.9974H11.3333Z"
                  fill="#101828"
                />
              </g>
              <defs>
                <clipPath id="clip0_13538_91499">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        )}
      </div>
      {isCensusModalOpen && (
        <EnterCensusModal
          day={periodDay}
          units={periodData?.units || []}
          setPeriodData={setPeriodData}
          onClose={() => setIsCensusModalOpen(false)}
        />
      )}
    </th>
  );
};

export default SchedulingDay;
